import React, { useCallback, useMemo, useState } from 'react';
import { useDispatch } from 'react-redux';

import { Box, Button, Typography } from '@mui/material';
import { DateFieldCDS } from '@sharedCR/coreDesignSystem/form';
import dayjs from 'dayjs';
import PropTypes from 'prop-types';

import { closeDialogAction } from 'store/global/actions';

import { DisclosureDeliveryTestIds } from '../../DisclosureDelivery.testIds';

const API_DATE_FORMAT = 'YYYY-MM-DD';

const MarkAsMailedDialog = ({ signerName = '', onSubmit }) => {
  const dispatch = useDispatch();

  const [mailedDate, setMailedDate] = useState(() => dayjs());
  const [isSubmitting, setIsSubmitting] = useState(false);

  const maxDate = useMemo(() => dayjs(), []);
  const isValid = !!mailedDate && mailedDate.isValid();

  const onChangeDate = useCallback(nextDate => setMailedDate(nextDate ?? null), []);

  const onClose = useCallback(() => dispatch(closeDialogAction()), [dispatch]);

  const onConfirm = useCallback(async () => {
    if (!mailedDate) {
      return;
    }

    setIsSubmitting(true);

    try {
      const succeeded = await onSubmit(mailedDate.format(API_DATE_FORMAT));

      if (succeeded) {
        dispatch(closeDialogAction());
      }
    } finally {
      setIsSubmitting(false);
    }
  }, [dispatch, mailedDate, onSubmit]);

  return (
    <Box className="flex flex-col items-stretch gap-12">
      <Typography color="text.secondary" variant="regular14">
        Record the date this disclosure package was mailed{signerName ? ` to ${signerName}` : ''}.
        The mailbox rule advances the earliest closing date from this date.
      </Typography>
      <DateFieldCDS
        dataTestId={DisclosureDeliveryTestIds.MARK_MAILED_DATE_INPUT}
        label="Mailed date"
        maxDate={maxDate}
        name="mailed-date"
        value={mailedDate}
        onChange={onChangeDate}
      />
      <Box className="pt-8 self-end flex gap-8">
        <Button
          data-testid={DisclosureDeliveryTestIds.MARK_MAILED_CANCEL_BUTTON}
          disabled={isSubmitting}
          variant="outlined"
          onClick={onClose}
        >
          Cancel
        </Button>
        <Button
          data-testid={DisclosureDeliveryTestIds.MARK_MAILED_SUBMIT_BUTTON}
          disabled={!isValid || isSubmitting}
          variant="contained"
          onClick={onConfirm}
        >
          Mark as mailed
        </Button>
      </Box>
    </Box>
  );
};

MarkAsMailedDialog.propTypes = {
  onSubmit: PropTypes.func.isRequired,
  signerName: PropTypes.string,
};

export default MarkAsMailedDialog;
